// Tactical map — the home screen and THE geo screen. Everything spatial lives
// here:
// - rough cache search zones + earned pins (tap one → dossier bottom sheet
//   with the full discover/claim flow)
// - own position, operation zone + out-of-bounds warning
// - every other agent's live position (everyone is visible by default)
// The HUD (intel, broadcasts, ranks, feed, help) rides along the top now that
// the map is home.
import { el, mount } from "../lib/dom";
import { currentUser } from "../lib/firebase";
import {
  watchGuest, watchSettings, watchCaches, watchCacheProgress, watchPositions,
  type Cache, type CacheProgress, type AppSettings, type Guest, type AgentPosition,
} from "../lib/data";
import { watchPosition } from "../lib/geo";
import { startSharing } from "../lib/location";
import { spyMap, blipIcon, agentIcon, drawCacheLayer, areaController, type SpyMap } from "../lib/map";
import { cacheCard } from "../components/cache-card";
import { nav } from "../components/nav";
import { hud, broadcastPanel } from "../components/hud";
import { showHelpOverlay } from "../components/help-overlay";
import { go } from "../router";

let unsubs: Array<() => void> = [];

// Agents whose last fix is older than this are treated as offline and dropped.
const STALE_MS = 5 * 60 * 1000;

function cleanup() {
  unsubs.forEach((u) => u());
  unsubs = [];
}

export async function renderMap(): Promise<void> {
  cleanup();
  const uid = currentUser()!.uid;

  // ---- HUD: intel + broadcasts + ranks + feed + help ----
  const hudEl = el("div");
  const panel = broadcastPanel(uid);

  // ---- map ----
  const mapEl = el("div", { style: "height:calc(100dvh - 260px);min-height:340px" });
  const mapWrap = el("div", { style: "position:relative;overflow:hidden;border-radius:var(--r)" }, mapEl);
  const legend = el("p.tiny.dim", {},
    "◉ you · ◉ other agents · ★/⚡ search zones — tap one to open its dossier · 🔫/🍺 pinned");

  // ---- cache dossier bottom sheet ----
  const sheetBody = el("div.sheet__body");
  const sheetClose = el("button.hud__bell", { style: "position:absolute;top:6px;right:8px;z-index:2" }, "✕");
  const sheet = el("div.sheet", {}, sheetClose, sheetBody);
  const backdrop = el("div.sheet-backdrop", {});
  let openCacheId: string | null = null;

  function closeSheet() {
    openCacheId = null;
    sheet.classList.remove("sheet--open");
    backdrop.classList.remove("sheet-backdrop--open");
  }
  sheetClose.addEventListener("click", closeSheet);
  backdrop.addEventListener("click", closeSheet);

  function renderSheet() {
    if (!openCacheId) return;
    const c = caches.find((x) => x.id === openCacheId);
    if (!c) { closeSheet(); return; }
    sheetBody.replaceChildren(cacheCard(c, progress[c.id] ?? {}, { uid, codename: () => myCodename }));
  }
  function openSheet(cacheId: string) {
    openCacheId = cacheId;
    renderSheet();
    sheet.classList.add("sheet--open");
    backdrop.classList.add("sheet-backdrop--open");
  }

  const lockedNote = el("div.card.card--locked", { style: "display:none" },
    el("span.card__tag", {}, "PROGRAM OFFLINE"),
    el("p.tiny.dim", {}, "Cache positions decrypt when the field hunt opens. Stand by, Agent."),
  );

  mount(el("div", {},
    hudEl,
    panel.el,
    el("div.card", {}, mapWrap, legend),
    lockedNote,
    backdrop,
    sheet,
    nav("/map"),
  ));

  // ---- state ----
  let settings: AppSettings | null = null;
  let guest: Guest | null = null;
  let caches: Cache[] = [];
  let progress: Record<string, CacheProgress> = {};
  let myCodename = "AGENT";
  let sm: SpyMap | null = null;
  let cacheLayer: ReturnType<SpyMap["L"]["layerGroup"]> | null = null;
  let agentsLayer: ReturnType<SpyMap["L"]["layerGroup"]> | null = null;
  let meMarker: ReturnType<SpyMap["L"]["marker"]> | null = null;

  function rebuildHud() {
    hudEl.replaceChildren(
      hud({
        intel: guest?.intelPoints ?? 0,
        onBell: () => panel.toggle(),
        unread: panel.unread(),
        onRanks: () => go("/leaderboard"),
        onFeed: () => go("/gallery"),
        onHelp: () => showHelpOverlay(true),
      }),
    );
  }

  function huntLive(): boolean {
    return settings?.fullHuntUnlocked === true;
  }
  function released(c: Cache): boolean {
    if (huntLive()) return true;
    const t = c.releaseAt?.toMillis?.() ?? Infinity;
    return t <= Date.now();
  }

  function redraw() {
    if (!sm || !cacheLayer) return;
    const visible = caches.filter(released);
    lockedNote.style.display = visible.length ? "none" : "";
    drawCacheLayer(sm, cacheLayer, visible, progress, openSheet);
    if (openCacheId) renderSheet(); // keep the open dossier in sync
  }

  // ---- plot every other agent's live position ----
  function plotAgents(positions: AgentPosition[]) {
    if (!sm || !agentsLayer) return;
    agentsLayer.clearLayers();
    const now = Date.now();
    for (const p of positions) {
      if (p.id === uid) continue; // self is the amber meMarker
      if (typeof p.lat !== "number" || typeof p.lng !== "number") continue;
      const age = p.updatedAt?.toMillis?.();
      if (typeof age === "number" && now - age > STALE_MS) continue;
      sm.L.marker([p.lat, p.lng], { icon: agentIcon(sm.L, p.codename ?? "AGENT") })
        .addTo(agentsLayer);
    }
  }

  rebuildHud();

  sm = await spyMap(mapEl, { zoom: 14 });
  cacheLayer = sm.L.layerGroup().addTo(sm.map);
  agentsLayer = sm.L.layerGroup().addTo(sm.map);
  const area = areaController(sm, mapWrap);
  redraw();

  // ---- subscriptions ----
  unsubs.push(panel.onChange(rebuildHud));
  unsubs.push(
    watchPosition((p) => {
      if (!sm) return;
      area.setPos(p);
      if (!meMarker) {
        meMarker = sm.L.marker([p.lat, p.lng], { icon: blipIcon(sm.L, "var(--amber)") })
          .addTo(sm.map)
          .bindPopup("You, Agent.");
      } else {
        meMarker.setLatLng([p.lat, p.lng]);
      }
    }),
  );
  unsubs.push(watchCaches((c) => { caches = c; redraw(); }));
  unsubs.push(watchCacheProgress(uid, (p) => { progress = p; redraw(); }));
  unsubs.push(watchPositions(plotAgents));
  unsubs.push(watchGuest(uid, (g) => {
    guest = g;
    myCodename = g?.displayName ?? "AGENT";
    rebuildHud();
    // Everyone is visible by default (no faction gate): once an agent has field
    // tracking on, they share and everyone sees them.
    if (g?.shareLocation) startSharing(uid, myCodename);
  }));
  let areaSet = false;
  unsubs.push(watchSettings((s) => {
    settings = s;
    area.setLive(s?.fullHuntUnlocked === true);
    if (!areaSet && s?.gameArea?.points?.length) {
      areaSet = true;
      area.setArea(s.gameArea.points);
    }
    redraw();
  }));
}
